import React, { useState } from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRight from '@mui/icons-material/ArrowRight';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ChatIcon from '@mui/icons-material/Chat';
import DynamicInfographic from '../../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../../components/navigation/ModuleSidebar';
import { scrollToTop } from '../../../utils/scrollUtils';
import PieChartIcon from '@mui/icons-material/PieChart';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import Radio from '@mui/material/Radio';
import AssessmentIcon from '@mui/icons-material/Assessment';
const InitialAssessment = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('5.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});
  
  const handleBack = () => {
    navigate('/courses/choking-management');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '5.1-1': 2, // Time, location, interventions performed, and patient's response
    '5.1-2': 1, // To provide a complete record of the incident and the care provided
    '5.1-3': 0, // The complication, its severity, and the medical response
    '5.2-1': 0, // Minor injury
    '5.2-2': 1, // Major injury
    '5.2-3': 2, // No injury
    '5.3-1': 2, // Dysphagia
    '5.3-2': 2, // 36 hours earlier
    '5.3-3': 2, // Specificity
    '5.4-1': 2, // It helps identify patterns and implement targeted improvements
    '5.4-2': 1, // Create formal mechanisms for sharing findings across departments
    '5.4-3': 0, // Regular review of choking incident data
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = answerIndex === correctAnswers[key];
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToTop();
  };
  
  // Define all sections for the module
  const sections = [
    {
      id: "5.1",
      title: "5.1 Post-Choking Documentation",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <TransferWithinAStationIcon sx={{ mr: 1, color: "#81c784" }} />
            5.1 Post-Choking Documentation
          </Typography>

          <Typography paragraph>
            Accurate documentation provides a detailed account of the choking incident and the clinical response. This information supports patient care, legal protection, and future quality improvement initiatives.
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 3,
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <AssessmentIcon sx={{ mr: 1 }} /> Documentation Impact Analysis
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  How Documentation Quality Affects Patient Outcomes
                </Typography>
                <Box sx={{ height: 240, position: "relative" }}>
                  {/* Coordinate system */}
                  <Box sx={{ position: "absolute", bottom: 20, left: 40, width: "calc(100% - 50px)", height: 1, bgcolor: "divider" }} />
                  <Box sx={{ position: "absolute", bottom: 20, left: 40, width: 1, height: 180, bgcolor: "divider" }} />
                  
                  {/* X-axis labels */}
                  <Typography variant="caption" sx={{ position: "absolute", bottom: 5, left: "50%", transform: "translateX(-50%)" }}>
                    Documentation Quality
                  </Typography>
                  
                  {/* Y-axis labels */}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: "absolute", 
                      left: 10, 
                      top: "50%", 
                      transform: "translateY(-50%) rotate(-90deg)",
                      transformOrigin: "center" 
                    }}
                  >
                    Positive Outcomes
                  </Typography>
                  
                  {/* Data area */}
                  <Box sx={{ 
                    position: "absolute", 
                    bottom: 20, 
                    left: 40, 
                    width: "calc(100% - 50px)", 
                    height: 180,
                    background: "linear-gradient(to top right, rgba(129, 199, 132, 0.1) 0%, rgba(129, 199, 132, 0.6) 100%)",
                    borderTop: "2px dashed #4caf50",
                    borderRight: "2px dashed #4caf50",
                  }} />
                  
                  {/* Data points with values */}
                  <Box sx={{ 
                    position: "absolute", 
                    bottom: 20, 
                    left: 40, 
                    width: "calc(100% - 50px)", 
                    height: 180,
                  }}>
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        bottom: "20%", 
                        left: "20%", 
                        width: 8, 
                        height: 8, 
                        bgcolor: "#d32f2f", 
                        borderRadius: "50%",
                        transform: "translate(-50%, 50%)"
                      }} 
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: "absolute", 
                        bottom: "25%", 
                        left: "20%", 
                        transform: "translate(-50%, 0)",
                        color: "#d32f2f", 
                        bgcolor: "rgba(255,255,255,0.7)",
                        px: 0.5
                      }}
                    >
                      38%
                    </Typography>
                    
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        bottom: "45%", 
                        left: "50%", 
                        width: 8, 
                        height: 8, 
                        bgcolor: "#ff9800", 
                        borderRadius: "50%",
                        transform: "translate(-50%, 50%)"
                      }} 
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: "absolute", 
                        bottom: "50%", 
                        left: "50%", 
                        transform: "translate(-50%, 0)",
                        color: "#ff9800",
                        bgcolor: "rgba(255,255,255,0.7)",
                        px: 0.5
                      }}
                    >
                      67%
                    </Typography>
                    
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        bottom: "70%", 
                        left: "80%", 
                        width: 8, 
                        height: 8, 
                        bgcolor: "#4caf50", 
                        borderRadius: "50%",
                        transform: "translate(-50%, 50%)"
                      }} 
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: "absolute", 
                        bottom: "75%", 
                        left: "80%", 
                        transform: "translate(-50%, 0)",
                        color: "#4caf50",
                        bgcolor: "rgba(255,255,255,0.7)", 
                        px: 0.5
                      }}
                    >
                      89%
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ display: "block", fontStyle: "italic", textAlign: "center" }}>
                    Higher documentation quality correlates with improved patient outcomes
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Documentation Elements Impact
                </Typography>
                <Box sx={{ height: 240 }}>
                  {/* Top row */}
                  <Box sx={{ display: "flex", height: "33%", mb: 1 }}>
                    <Box 
                      sx={{ 
                        flex: 1,
                        mr: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center", 
                        justifyContent: "center",
                        borderRadius: 1,
                        bgcolor: "#e8f5e9",
                        border: "1px solid #a5d6a7",
                        position: "relative",
                        p: 1
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#2e7d32" }}>92%</Typography>
                      <Typography variant="caption" align="center">
                        Time & Location
                      </Typography>
                      <Box 
                        sx={{ 
                          position: "absolute", 
                          top: 0, 
                          left: 0, 
                          width: "92%", 
                          height: 4,
                          bgcolor: "#4caf50",
                          borderTopLeftRadius: 1
                        }}
                      />
                    </Box>
                    <Box 
                      sx={{ 
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center", 
                        justifyContent: "center",
                        borderRadius: 1,
                        bgcolor: "#fff8e1",
                        border: "1px solid #ffe082",
                        position: "relative",
                        p: 1
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#f57f17" }}>71%</Typography>
                      <Typography variant="caption" align="center">
                        Patient Condition
                      </Typography>
                      <Box 
                        sx={{ 
                          position: "absolute", 
                          top: 0, 
                          left: 0, 
                          width: "71%", 
                          height: 4,
                          bgcolor: "#ffc107",
                          borderTopLeftRadius: 1
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Middle row */}
                  <Box sx={{ display: "flex", height: "33%", mb: 1 }}>
                    <Box 
                      sx={{ 
                        flex: 1,
                        mr: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center", 
                        justifyContent: "center",
                        borderRadius: 1,
                        bgcolor: "#e3f2fd",
                        border: "1px solid #90caf9",
                        position: "relative",
                        p: 1
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0d47a1" }}>87%</Typography>
                      <Typography variant="caption" align="center">
                        Interventions
                      </Typography>
                      <Box 
                        sx={{ 
                          position: "absolute", 
                          top: 0, 
                          left: 0, 
                          width: "87%", 
                          height: 4,
                          bgcolor: "#2196f3",
                          borderTopLeftRadius: 1
                        }}
                      />
                    </Box>
                    <Box 
                      sx={{ 
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center", 
                        justifyContent: "center",
                        borderRadius: 1,
                        bgcolor: "#f3e5f5",
                        border: "1px solid #ce93d8",
                        position: "relative",
                        p: 1
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#6a1b9a" }}>84%</Typography>
                      <Typography variant="caption" align="center">
                        Response
                      </Typography>
                      <Box 
                        sx={{ 
                          position: "absolute", 
                          top: 0, 
                          left: 0, 
                          width: "84%", 
                          height: 4,
                          bgcolor: "#9c27b0",
                          borderTopLeftRadius: 1
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Bottom row */}
                  <Box sx={{ display: "flex", height: "33%" }}>
                    <Box 
                      sx={{ 
                        flex: 1,
                        mr: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center", 
                        justifyContent: "center",
                        borderRadius: 1,
                        bgcolor: "#ffebee",
                        border: "1px solid #ef9a9a",
                        position: "relative",
                        p: 1
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#b71c1c" }}>65%</Typography>
                      <Typography variant="caption" align="center">
                        Complications
                      </Typography>
                      <Box 
                        sx={{ 
                          position: "absolute", 
                          top: 0, 
                          left: 0, 
                          width: "65%", 
                          height: 4,
                          bgcolor: "#f44336",
                          borderTopLeftRadius: 1
                        }}
                      />
                    </Box>
                    <Box 
                      sx={{ 
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center", 
                        justifyContent: "center",
                        borderRadius: 1,
                        bgcolor: "#ede7f6",
                        border: "1px solid #b39ddb",
                        position: "relative",
                        p: 1
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#4527a0" }}>79%</Typography>
                      <Typography variant="caption" align="center">
                        Follow-up
                      </Typography>
                      <Box 
                        sx={{ 
                          position: "absolute", 
                          top: 0, 
                          left: 0, 
                          width: "79%", 
                          height: 4,
                          bgcolor: "#673ab7",
                          borderTopLeftRadius: 1
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ display: "block", fontStyle: "italic", textAlign: "center" }}>
                    Documentation completion rate by element (% of cases with complete information)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Typography variant="caption" sx={{ display: "block", mt: 2, fontStyle: "italic", color: "text.secondary" }}>
              Data based on analysis of 500+ choking incident reports from healthcare facilities. Complete and accurate documentation is associated with 89% positive patient outcomes versus 38% for poorly documented cases.
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: "rgba(129, 199, 132, 0.1)",
              p: 2,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Key Components of Documentation
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "white" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                      Time and Location
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Record exact time and place" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Include environment details" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "white" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                      Initial Assessment
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Patient's initial condition" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Type of airway obstruction" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "white" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                      Interventions and Response
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Specific interventions used" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Patient's response to intervention" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "white" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                      Follow-up and Complications
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Document any complications" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Record ongoing symptoms" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 5.1 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What key information should be documented immediately after a choking event?
                    </Typography>
                    <List dense>
                      {[
                        "Time, location, and outcome only",
                        "Time, location, interventions performed, and patient's response",
                        "Only the type of intervention used",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["5.1-1"] === index) {
                          if (index === correctAnswers["5.1-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (showExplanations["5.1-1"] && index === correctAnswers["5.1-1"]) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() => handleAnswerSelect("5.1", "1", index)}
                            disabled={answerStatus["5.1-1"] === true && index !== correctAnswers["5.1-1"]}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: answerStatus["5.1-1"] === true && index !== correctAnswers["5.1-1"] ? 0.6 : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["5.1-1"] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: "rgba(129, 199, 132, 0.1)", borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Complete documentation should cover all relevant aspects of the event, including the setting, initial assessment, interventions, and patient outcome.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is it important to document communication with the patient's family and attending physician?
                    </Typography>
                    <List dense>
                      {[
                        "To ensure billing accuracy",
                        "To verify that the family was notified",
                        "To provide a complete record of the incident and the care provided",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["5.1-2"] === index) {
                          if (index === correctAnswers["5.1-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (showExplanations["5.1-2"] && index === correctAnswers["5.1-2"]) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() => handleAnswerSelect("5.1", "2", index)}
                            disabled={answerStatus["5.1-2"] === true && index !== correctAnswers["5.1-2"]}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: answerStatus["5.1-2"] === true && index !== correctAnswers["5.1-2"] ? 0.6 : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["5.1-2"] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: "rgba(129, 199, 132, 0.1)", borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Recording communication with the family and physician ensures accountability and supports continuity of care.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What should be documented if the patient experiences a complication after choking?
                    </Typography>
                    <List dense>
                      {[
                        "Only the intervention used",
                        "The complication, its severity, and the medical response",
                        "The patient's name and age",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["5.1-3"] === index) {
                          if (index === correctAnswers["5.1-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (showExplanations["5.1-3"] && index === correctAnswers["5.1-3"]) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() => handleAnswerSelect("5.1", "3", index)}
                            disabled={answerStatus["5.1-3"] === true && index !== correctAnswers["5.1-3"]}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: answerStatus["5.1-3"] === true && index !== correctAnswers["5.1-3"] ? 0.6 : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["5.1-3"] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: "rgba(129, 199, 132, 0.1)", borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Documenting complications and follow-up care ensures accurate tracking and helps identify opportunities for improving clinical practice.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "5.2",
      title: "5.2 Incident Classification",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <AccessibilityNewIcon sx={{ mr: 1, color: "#81c784" }} />
            5.2 Incident Classification
          </Typography>

          <Typography paragraph>
            Classifying choking incidents helps healthcare facilities track trends, identify risks, and improve response protocols. Classification provides a structured way to measure the severity of the event and the patient's outcome.
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 3,
              border: "1px solid rgba(76, 175, 80, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(76, 175, 80, 0.5)",
                pb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <PieChartIcon sx={{ mr: 1 }} /> Incident Classification Analysis
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Incident Distribution by Severity (Past 12 Months)
                </Typography>
                <Box
                  sx={{
                    height: 280,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* Pie chart visualization */}
                  <Box sx={{ position: "relative", width: 220, height: 220 }}>
                    {/* Severity level 1 (No Injury) - Green */}
                    <Box 
                      sx={{ 
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "#4caf50",
                        clipPath: "polygon(50% 50%, 50% 0%, 100% 0%, 100% 100%, 50% 100%, 50% 50%)",
                        borderRadius: "50%",
                      }}
                    />
                    
                    {/* Severity level 2 (Minor Injury) - Light Green */}
                    <Box 
                      sx={{ 
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "#8bc34a",
                        clipPath: "polygon(50% 50%, 50% 100%, 0% 100%, 0% 30%, 30% 0%, 50% 0%, 50% 50%)",
                        borderRadius: "50%", 
                      }}
                    />
                    
                    {/* Severity level 3 (Major Injury) - Yellow */}
                    <Box 
                      sx={{ 
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "#ffc107",
                        clipPath: "polygon(50% 50%, 30% 0%, 0% 30%, 0% 0%, 0% 0%, 0% 0%, 50% 50%)",
                        borderRadius: "50%", 
                      }}
                    />
                    
                    {/* Severity level 4 (Fatal) - Red */}
                    <Box 
                      sx={{ 
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "#f44336",
                        clipPath: "polygon(50% 50%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 50% 50%)",
                        borderRadius: "50%", 
                      }}
                    />
                    
                    {/* White center */}
                    <Box 
                      sx={{ 
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 70,
                        height: 70,
                        background: "white",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>Total</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2e7d32" }}>428</Typography>
                      <Typography variant="caption">incidents</Typography>
                    </Box>
                  </Box>
                  
                  {/* Labels */}
                  <Box sx={{ 
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    pr: 2,
                    py: 2,
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#4caf50", mr: 1 }}></Box>
                      <Typography variant="caption">No Injury: 65% (278)</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#8bc34a", mr: 1 }}></Box>
                      <Typography variant="caption">Minor Injury: 25% (107)</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#ffc107", mr: 1 }}></Box>
                      <Typography variant="caption">Major Injury: 9% (39)</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#f44336", mr: 1 }}></Box>
                      <Typography variant="caption">Fatal Outcome: 1% (4)</Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Key Performance Indicators
                </Typography>
                <Box sx={{ height: 280, display: "flex", flexDirection: "column", justifyContent: "space-between", p: 1 }}>
                  {/* KPI 1 - Response Time */}
                  <Box sx={{ 
                    py: 2, 
                    px: 3, 
                    borderRadius: 2, 
                    bgcolor: "#e8f5e9", 
                    border: "1px solid #c8e6c9",
                    position: "relative",
                    overflow: "hidden",
                  }}>
                    <Typography variant="subtitle2" sx={{ color: "#2e7d32", fontWeight: 600, mb: 1, zIndex: 2, position: "relative" }}>
                      Average Response Time
                    </Typography>
                    <Typography variant="h4" sx={{ color: "#1b5e20", fontWeight: 600, zIndex: 2, position: "relative" }}>
                      28<Typography component="span" variant="body2" sx={{ verticalAlign: "top", ml: 0.5 }}>seconds</Typography>
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "#2e7d32", zIndex: 2, position: "relative" }}>
                      <ArrowDownwardIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                      12% improvement from last year
                    </Typography>
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        right: -20, 
                        bottom: -20, 
                        width: 100, 
                        height: 100, 
                        borderRadius: "50%", 
                        bgcolor: "rgba(76, 175, 80, 0.2)" 
                      }}
                    />
                  </Box>
                  
                  {/* KPI 2 - Incident Reporting */}
                  <Box sx={{ 
                    py: 2, 
                    px: 3, 
                    borderRadius: 2, 
                    bgcolor: "#e3f2fd", 
                    border: "1px solid #bbdefb",
                    position: "relative",
                    overflow: "hidden",
                  }}>
                    <Typography variant="subtitle2" sx={{ color: "#0d47a1", fontWeight: 600, mb: 1, zIndex: 2, position: "relative" }}>
                      Documentation Compliance
                    </Typography>
                    <Typography variant="h4" sx={{ color: "#1565c0", fontWeight: 600, zIndex: 2, position: "relative" }}>
                      94<Typography component="span" variant="body2" sx={{ verticalAlign: "top", ml: 0.5 }}>%</Typography>
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "#1565c0", zIndex: 2, position: "relative" }}>
                      <ArrowUpwardIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                      8% improvement from last year
                    </Typography>
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        right: -20, 
                        bottom: -20, 
                        width: 100, 
                        height: 100, 
                        borderRadius: "50%", 
                        bgcolor: "rgba(33, 150, 243, 0.2)" 
                      }}
                    />
                  </Box>
                  
                  {/* KPI 3 - Staff Training */}
                  <Box sx={{ 
                    py: 2, 
                    px: 3, 
                    borderRadius: 2, 
                    bgcolor: "#fff8e1", 
                    border: "1px solid #ffecb3",
                    position: "relative",
                    overflow: "hidden",
                  }}>
                    <Typography variant="subtitle2" sx={{ color: "#f57f17", fontWeight: 600, mb: 1, zIndex: 2, position: "relative" }}>
                      Staff Certified in Response
                    </Typography>
                    <Typography variant="h4" sx={{ color: "#ff8f00", fontWeight: 600, zIndex: 2, position: "relative" }}>
                      98<Typography component="span" variant="body2" sx={{ verticalAlign: "top", ml: 0.5 }}>%</Typography>
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "#ff8f00", zIndex: 2, position: "relative" }}>
                      <ArrowUpwardIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                      5% improvement from last year
                    </Typography>
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        right: -20, 
                        bottom: -20, 
                        width: 100, 
                        height: 100, 
                        borderRadius: "50%", 
                        bgcolor: "rgba(255, 193, 7, 0.2)" 
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
            
            <Typography variant="caption" sx={{ display: "block", mt: 2, fontStyle: "italic", color: "text.secondary" }}>
              Data aggregated from 428 reported choking incidents across 15 healthcare facilities. The classification system has helped identify high-risk scenarios and implement targeted prevention strategies.
            </Typography>
          </Box>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(129, 199, 132, 0.1)",
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600, borderBottom: "1px solid rgba(129, 199, 132, 0.5)", pb: 1 }}>
              Types of Incident Classification
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box sx={{ minWidth: 40, height: 40, borderRadius: "50%", bgcolor: "rgba(129, 199, 132, 0.2)", display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                    <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>1</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}>
                      No Injury
                    </Typography>
                    <Typography variant="body2">
                      The patient experiences no lasting symptoms. The airway is cleared, and the patient returns to their baseline condition without medical complications.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box sx={{ minWidth: 40, height: 40, borderRadius: "50%", bgcolor: "rgba(129, 199, 132, 0.2)", display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                    <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>2</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}>
                      Minor Injury
                    </Typography>
                    <Typography variant="body2">
                      The patient experiences mild symptoms such as coughing or throat irritation. Symptoms resolve quickly without requiring hospitalization.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box sx={{ minWidth: 40, height: 40, borderRadius: "50%", bgcolor: "rgba(129, 199, 132, 0.2)", display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                    <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>3</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}>
                      Major Injury
                    </Typography>
                    <Typography variant="body2">
                      The patient experiences significant symptoms like oxygen desaturation, difficulty breathing, or aspiration. Medical intervention is required, and hospitalization may be necessary.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box sx={{ minWidth: 40, height: 40, borderRadius: "50%", bgcolor: "rgba(129, 199, 132, 0.2)", display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                    <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>4</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}>
                      Fatal Outcome
                    </Typography>
                    <Typography variant="body2">
                      The patient does not survive despite all resuscitative efforts. A detailed post-mortem analysis is required to identify possible causes and preventive measures.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 5.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What type of incident classification applies when a patient experiences brief coughing but returns to normal without medical assistance?
                    </Typography>
                    <List dense>
                      {[
                        "No injury",
                        "Minor injury",
                        "Major injury",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["5.2-1"] === index) {
                          if (index === correctAnswers["5.2-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (showExplanations["5.2-1"] && index === correctAnswers["5.2-1"]) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() => handleAnswerSelect("5.2", "1", index)}
                            disabled={answerStatus["5.2-1"] === true && index !== correctAnswers["5.2-1"]}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: answerStatus["5.2-1"] === true && index !== correctAnswers["5.2-1"] ? 0.6 : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["5.2-1"] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: "rgba(129, 199, 132, 0.1)", borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Mild coughing without complications is considered a minor injury.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      If a patient requires hospitalization after a choking incident, how should the event be classified?
                    </Typography>
                    <List dense>
                      {[
                        "No injury",
                        "Minor injury",
                        "Major injury",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["5.2-2"] === index) {
                          if (index === correctAnswers["5.2-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (showExplanations["5.2-2"] && index === correctAnswers["5.2-2"]) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() => handleAnswerSelect("5.2", "2", index)}
                            disabled={answerStatus["5.2-2"] === true && index !== correctAnswers["5.2-2"]}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: answerStatus["5.2-2"] === true && index !== correctAnswers["5.2-2"] ? 0.6 : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["5.2-2"] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: "rgba(129, 199, 132, 0.1)", borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Hospitalization indicates a serious outcome that requires further medical care.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      If a patient's airway is cleared and they experience no further symptoms, how should the incident be classified?
                    </Typography>
                    <List dense>
                      {[
                        "No injury",
                        "Minor injury",
                        "Major injury",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["5.2-3"] === index) {
                          if (index === correctAnswers["5.2-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (showExplanations["5.2-3"] && index === correctAnswers["5.2-3"]) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() => handleAnswerSelect("5.2", "3", index)}
                            disabled={answerStatus["5.2-3"] === true && index !== correctAnswers["5.2-3"]}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: answerStatus["5.2-3"] === true && index !== correctAnswers["5.2-3"] ? 0.6 : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["5.2-3"] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: "rgba(129, 199, 132, 0.1)", borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A resolved choking incident without complications is classified as "no injury."
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "5.3",
      title: "5.3 Data Analytics for Prevention",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <AnalyticsIcon sx={{ mr: 1, color: "#81c784" }} />
            5.3 Data Analytics for Prevention
          </Typography>

          <Typography paragraph>
            Analyzing data from choking incidents can reveal patterns and risk factors that enable healthcare facilities to implement targeted prevention strategies. Predictive analytics can identify high-risk patients and situations before incidents occur.
          </Typography>

          {/* Predictive Analytics Dashboard */}
          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 3,
              border: "1px solid rgba(76, 175, 80, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(76, 175, 80, 0.5)",
                pb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <BarChartIcon sx={{ mr: 1 }} /> Predictive Analytics Dashboard
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Risk Factor Correlation with Choking Incidents
                </Typography>
                <Box
                  sx={{
                    height: 280,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* Horizontal bar chart visualization */}
                  <Box sx={{ 
                    width: "100%", 
                    height: "100%", 
                    position: "relative",
                    pt: 3,
                    pb: 5,
                    pl: 5,
                    pr: 2
                  }}>
                    {/* Y-axis */}
                    <Box sx={{ 
                      position: "absolute", 
                      left: 0, 
                      top: 0, 
                      height: "100%", 
                      width: 1, 
                      bgcolor: "divider"
                    }} />
                    
                    {/* X-axis */}
                    <Box sx={{ 
                      position: "absolute", 
                      left: 0, 
                      bottom: 0, 
                      width: "100%", 
                      height: 1, 
                      bgcolor: "divider"
                    }} />
                    
                    {/* X-axis labels */}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: "absolute", 
                        bottom: 0, 
                        left: "50%", 
                        transform: "translateX(-50%) translateY(16px)",
                      }}
                    >
                      Correlation Strength (%)
                    </Typography>
                    
                    {/* Risk factor bars */}
                    <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
                      {/* Dysphagia */}
                      <Box sx={{ 
                        position: "absolute", 
                        left: 0, 
                        top: "10%", 
                        height: 24, 
                        width: "85%", 
                        bgcolor: "#4caf50",
                        borderRadius: "0 4px 4px 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        pr: 1,
                      }}>
                        <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>85%</Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          left: -4,
                          top: "10%",
                          transform: "translateX(-100%) translateY(12px)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Dysphagia
                      </Typography>
                      
                      {/* Cognitive impairment */}
                      <Box sx={{ 
                        position: "absolute", 
                        left: 0, 
                        top: "30%", 
                        height: 24, 
                        width: "72%", 
                        bgcolor: "#66bb6a",
                        borderRadius: "0 4px 4px 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        pr: 1,
                      }}>
                        <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>72%</Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          left: -4,
                          top: "30%",
                          transform: "translateX(-100%) translateY(12px)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Cognitive impairment
                      </Typography>
                      
                      {/* Poor dentition */}
                      <Box sx={{ 
                        position: "absolute", 
                        left: 0, 
                        top: "50%", 
                        height: 24, 
                        width: "58%", 
                        bgcolor: "#81c784",
                        borderRadius: "0 4px 4px 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        pr: 1,
                      }}>
                        <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>58%</Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          left: -4,
                          top: "50%",
                          transform: "translateX(-100%) translateY(12px)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Poor dentition
                      </Typography>
                      
                      {/* Medication side effects */}
                      <Box sx={{ 
                        position: "absolute", 
                        left: 0, 
                        top: "70%", 
                        height: 24, 
                        width: "45%", 
                        bgcolor: "#a5d6a7",
                        borderRadius: "0 4px 4px 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        pr: 1,
                      }}>
                        <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>45%</Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          left: -4,
                          top: "70%",
                          transform: "translateX(-100%) translateY(12px)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Medication side effects
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Prediction Accuracy Metrics
                </Typography>
                <Box sx={{ height: 280, display: "flex", flexDirection: "column", justifyContent: "space-between", p: 1 }}>
                  {/* Circular progress indicators */}
                  <Box sx={{ display: "flex", justifyContent: "space-around", my: 2 }}>
                    {/* Model Accuracy */}
                    <Box sx={{ textAlign: "center" }}>
                      <Box sx={{ 
                        position: "relative", 
                        width: 80, 
                        height: 80,
                        borderRadius: "50%",
                        background: "conic-gradient(#4caf50 87%, #e8f5e9 0)",
                        transform: "rotate(-90deg)",
                        mx: "auto"
                      }}>
                        <Box sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%) rotate(90deg)",
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          bgcolor: "background.paper",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#2e7d32" }}>87%</Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ display: "block", mt: 1 }}>Model Accuracy</Typography>
                    </Box>
                    
                    {/* Sensitivity */}
                    <Box sx={{ textAlign: "center" }}>
                      <Box sx={{ 
                        position: "relative", 
                        width: 80, 
                        height: 80,
                        borderRadius: "50%",
                        background: "conic-gradient(#2196f3 82%, #e3f2fd 0)",
                        transform: "rotate(-90deg)",
                        mx: "auto"
                      }}>
                        <Box sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%) rotate(90deg)",
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          bgcolor: "background.paper",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#1565c0" }}>82%</Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ display: "block", mt: 1 }}>Sensitivity</Typography>
                    </Box>
                    
                    {/* Specificity */}
                    <Box sx={{ textAlign: "center" }}>
                      <Box sx={{ 
                        position: "relative", 
                        width: 80, 
                        height: 80,
                        borderRadius: "50%",
                        background: "conic-gradient(#ff9800 91%, #fff3e0 0)",
                        transform: "rotate(-90deg)",
                        mx: "auto"
                      }}>
                        <Box sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%) rotate(90deg)",
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          bgcolor: "background.paper",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#e65100" }}>91%</Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ display: "block", mt: 1 }}>Specificity</Typography>
                    </Box>
                  </Box>
                  
                  {/* Prediction timeline */}
                  <Box sx={{ 
                    bgcolor: "#f1f8e9", 
                    p: 2, 
                    borderRadius: 2, 
                    mt: 2,
                    border: "1px dashed #81c784"
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#2e7d32" }}>
                      Prediction Timeline
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="caption" sx={{ color: "#2e7d32" }}>
                          Current risk assessment
                        </Typography>
                        <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>
                          12h
                        </Typography>
                      </Box>
                      <ArrowRightAltIcon sx={{ color: "#7cb342" }} />
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="caption" sx={{ color: "#2e7d32" }}>
                          Predictive model
                        </Typography>
                        <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>
                          36h
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ArrowUpwardIcon sx={{ color: "#2e7d32", fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: "#2e7d32", fontWeight: 600 }}>
                          200%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            
            <Typography variant="caption" sx={{ display: "block", mt: 2, fontStyle: "italic", color: "text.secondary" }}>
              Data collected from 1,250 patients across multiple care facilities. The predictive model can identify high-risk patients up to 36 hours before potential incidents, providing a critical window for intervention.
            </Typography>
          </Box>

          <Typography paragraph>
            When implemented effectively, data analytics can transform choking prevention from reactive to proactive. By identifying patterns in historical data, healthcare providers can implement targeted interventions before incidents occur.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                    Benefits of Predictive Analytics
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Earlier Intervention" 
                        secondary="Predict high-risk situations hours or days before an incident might occur" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Resource Optimization" 
                        secondary="Direct monitoring and preventive resources to patients with the highest risk factors"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Pattern Recognition" 
                        secondary="Identify non-obvious correlations between factors that human analysis might miss"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Continuous Learning" 
                        secondary="Models improve over time as more data is collected and outcomes are recorded"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                    Implementation Strategies
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Data Integration" 
                        secondary="Combine patient records, assessment tools, and incident reports into a unified database" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Alert Systems" 
                        secondary="Develop notification protocols when predictive models identify high-risk patients"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Staff Training" 
                        secondary="Educate staff on how to interpret and respond to predictive analytics data"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Validation Studies" 
                        secondary="Regularly assess model accuracy and refine algorithms based on real-world outcomes"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Knowledge Check */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Knowledge Check
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which risk factor shows the strongest correlation with choking incidents according to the data?
                    </Typography>
                    <List dense>
                      {[
                        "Medication side effects",
                        "Poor dentition",
                        "Dysphagia",
                      ].map((option, index) => (
                        <ListItem key={index} dense>
                          <ListItemIcon>
                            <Radio
                              checked={quizAnswers["5.3-1"] === index}
                              onChange={() => handleAnswerChange("5.3-1", index)}
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText primary={option} />
                        </ListItem>
                      ))}
                    </List>
                    {showExplanations["5.3-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Dysphagia (difficulty swallowing) shows the strongest correlation at 85%, making it the most significant risk factor to monitor for preventing choking incidents.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      How much earlier can the predictive model identify high-risk patients compared to current assessment methods?
                    </Typography>
                    <List dense>
                      {[
                        "12 hours earlier",
                        "24 hours earlier",
                        "36 hours earlier",
                        "Implement all identified changes at once for maximum impact",
                        "Create formal mechanisms for sharing findings across departments",
                        "Focus exclusively on staff training to prevent future incidents",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["5.4-2"] === index) {
                          if (index === correctAnswers["5.4-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["5.4-2"] &&
                          index === correctAnswers["5.4-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("5.4", "2", index)
                            }
                            disabled={
                              answerStatus["5.4-2"] === true &&
                              index !== correctAnswers["5.4-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["5.4-2"] === true &&
                                index !== correctAnswers["5.4-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["5.4-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Sharing findings across departments ensures that all relevant stakeholders are aware of identified issues and can collaborate on developing comprehensive solutions that address systemic problems.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is the most important indicator to track for measuring the success of quality improvement efforts?
                    </Typography>
                    <List dense>
                      {[
                        "Regular review of choking incident data",
                        "Number of staff attending training sessions",
                        "Cost savings from reduced hospital stays",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["5.4-3"] === index) {
                          if (index === correctAnswers["5.4-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["5.4-3"] &&
                          index === correctAnswers["5.4-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("5.4", "3", index)
                            }
                            disabled={
                              answerStatus["5.4-3"] === true &&
                              index !== correctAnswers["5.4-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["5.4-3"] === true &&
                                index !== correctAnswers["5.4-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["5.4-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Regular review of incident data provides direct insight into whether improvement efforts are actually reducing choking events, which is the primary goal of quality improvement initiatives in this area.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
  ];
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: "#81c784", fontWeight: 600 }}
        >
          Module 5: Documentation, Reporting, and Analysis
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
          Develop skills to accurately document events, report incidents, and
          analyze outcomes for quality improvement.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <ModuleSidebar
              sections={sections}
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              moduleTitle="Caregiver Best Practices"
            />
          </Grid>
          <Grid item xs={12} md={9}>
            {sections.find((section) => section.id === activeSection)?.content}

            {/* Module Buttons */}
            <Box
              sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back to Learning Modules
              </Button>
              {activeSection !== sections[sections.length - 1].id && (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleNextSection}
                  sx={{ bgcolor: "#81c784", "&:hover": { bgcolor: "#4caf50" } }}
                >
                  Next Section
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default InitialAssessment;
